import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, useTableSort, SortableTH } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UserPlus, KeyRound, Unlock, ShieldCheck, Trash2, RotateCcw, Mail, Clock, Link2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = { employee: "Employee", manager: "Manager", hr_admin: "HR Admin", admin: "Administrator" };
const PERMISSION_DEFS = [
  ["view_salary", "View salary"],
  ["manage_salary", "Manage salary & history"],
  ["view_cases", "View HR cases"],
  ["manage_cases", "Manage HR cases"],
  ["manage_documents", "Manage documents"],
];

export default function Users() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "employee", password: "", employee_id: "none", send_invite: true });
  const [reset, setReset] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [dr, setDr] = useState(null);
  const [reassignTo, setReassignTo] = useState("none");
  const [deleted, setDeleted] = useState([]);
  const [newPw, setNewPw] = useState("");
  const [perms, setPerms] = useState(null); // user being edited for permissions
  const [linkUser, setLinkUser] = useState(null); // user being re-linked to an employee
  const [linkEmp, setLinkEmp] = useState("none");

  const load = () => { api.get("/users").then((r) => setRows(r.data)); api.get("/employees").then((r) => setEmployees(r.data)); api.get("/users/deleted").then((r) => setDeleted(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", { ...f, employee_id: f.employee_id === "none" ? null : f.employee_id }); toast.success(f.send_invite ? "User created — invite email sent" : "User created"); setOpen(false); setF({ name: "", email: "", role: "employee", password: "", employee_id: "none", send_invite: true }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const inviteUser = async (u) => { try { const r = await api.post(`/users/${u.id}/invite`); toast.success(r.data?.message || `Invite sent to ${u.email}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const openLink = (u) => { setLinkUser(u); setLinkEmp(u.employee_id || "none"); };
  const saveLink = async () => { try { await api.put(`/users/${linkUser.id}`, { employee_id: linkEmp === "none" ? "" : linkEmp }); toast.success("Employee link updated"); setLinkUser(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const empName = (id) => { const e = employees.find((x) => x.id === id); return e ? `${e.first_name} ${e.last_name}` : null; };
  const toggleActive = async (u) => { await api.put(`/users/${u.id}`, { active: !u.active }); load(); };
  const changeRole = async (u, role) => { await api.put(`/users/${u.id}`, { role }); toast.success("Role updated"); load(); };
  const doReset = async () => { await api.put(`/users/${reset.id}`, { reset_password: newPw }); toast.success("Password reset"); setReset(null); setNewPw(""); };
  const openDelete = async (u) => {
    setToDelete(u); setReassignTo("none"); setDr(null);
    try { const r = await api.get(`/users/${u.id}/direct-reports`); setDr(r.data); }
    catch { setDr({ count: 0, reports: [], managers: [] }); }
  };
  const removeUser = async () => { try { const q = (dr && dr.count > 0) ? `?reassign_to=${encodeURIComponent(reassignTo)}` : ""; const r = await api.delete(`/users/${toDelete.id}${q}`); toast.success(r.data?.message || `${toDelete.email} moved to deleted`); setToDelete(null); setDr(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const restoreUser = async (u) => { try { await api.post(`/users/${u.id}/restore`); toast.success(`${u.email} restored`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const purgeUser = async (u) => { if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return; try { await api.delete(`/users/${u.id}/permanent`); toast.success(`${u.email} permanently deleted`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const unlockUser = async (u) => { try { await api.put(`/users/${u.id}`, { unlock: true }); toast.success(`${u.email} unlocked`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const togglePerm = async (p) => {
    const has = (perms.permissions || []).includes(p);
    const next = has ? perms.permissions.filter((x) => x !== p) : [...(perms.permissions || []), p];
    try { await api.put(`/users/${perms.id}`, { permissions: next }); const updated = { ...perms, permissions: next }; setPerms(updated); setRows((rs) => rs.map((r) => (r.id === perms.id ? updated : r))); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const { sorted, sort, toggle } = useTableSort(rows, {
    name: (u) => u.name, email: (u) => u.email, role: (u) => u.role, active: (u) => (u.active ? 1 : 0),
  }, { key: "name", dir: "asc" });

  return (
    <div className="space-y-5">
      <PageHeader title="Users & Access" subtitle="Manage login accounts, roles and access">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="add-user-btn"><UserPlus size={16} className="mr-2" />Create user</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create user account</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
              <div><Label className="text-xs text-slate-500">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></div>
              <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer" data-testid="invite-toggle-row">
                <Switch checked={f.send_invite} onCheckedChange={(v) => setF({ ...f, send_invite: v })} data-testid="user-send-invite-toggle" className="mt-0.5" />
                <span className="text-sm">
                  <span className="font-medium text-[#0B1A2F] flex items-center gap-1.5"><Mail size={14} className="text-[#2EA3FD]" />Invite user via email</span>
                  <span className="text-xs text-slate-500">They'll receive a branded email to set their own password. No temporary password is created.</span>
                </span>
              </label>
              {!f.send_invite && (
                <div><Label className="text-xs text-slate-500">Temporary password</Label><Input value={f.password} placeholder="Welcome2026!" onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-temp-password-input" /></div>
              )}
              <div><Label className="text-xs text-slate-500">Role</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="hr_admin">HR Admin</SelectItem><SelectItem value="admin">Administrator</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Link to employee</Label>
                <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-[#2EA3FD]" data-testid="user-save-btn">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto cass-scroll">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <SortableTH label="Name" sortId="name" sort={sort} onSort={toggle} />
            <SortableTH label="Email" sortId="email" sort={sort} onSort={toggle} />
            <SortableTH label="Role" sortId="role" sort={sort} onSort={toggle} />
            <SortableTH label="Active" sortId="active" sort={sort} onSort={toggle} />
            <TableHead className="text-xs uppercase text-slate-600">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sorted.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="hr_admin">HR Admin</SelectItem><SelectItem value="admin">Administrator</SelectItem></SelectContent>
                    </Select>
                    {u.invite_pending && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[11px] font-medium" data-testid={`invite-pending-${u.email}`}><Clock size={11} />Invite pending</span>}
                  </div>
                </TableCell>
                <TableCell><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`user-active-${u.email}`} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => inviteUser(u)} data-testid={`invite-user-${u.email}`}><Mail size={14} className="mr-1" />{u.invite_pending ? "Resend invite" : "Invite"}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setPerms(u)} data-testid={`perms-user-${u.email}`}><ShieldCheck size={14} className="mr-1" />Permissions</Button>
                    <Button variant="ghost" size="sm" onClick={() => openLink(u)} data-testid={`link-user-${u.email}`}><Link2 size={14} className="mr-1" />Employee</Button>
                    <Button variant="ghost" size="sm" onClick={() => unlockUser(u)} data-testid={`unlock-user-${u.email}`}><Unlock size={14} className="mr-1" />Unlock</Button>
                    <Button variant="ghost" size="sm" onClick={() => setReset(u)} data-testid={`reset-user-${u.email}`}><KeyRound size={14} className="mr-1" />Reset password</Button>
                    <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => openDelete(u)} data-testid={`delete-user-${u.email}`}><Trash2 size={14} className="mr-1" />Delete</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {deleted.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="deleted-users-panel">
          <div className="px-5 py-3 border-b border-slate-100"><h3 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><Trash2 size={16} className="text-slate-400" />Recently deleted</h3><p className="text-xs text-slate-400">Deleted accounts are kept for 30 days so you can undo mistakes. Restore reactivates the login.</p></div>
          <div className="divide-y divide-slate-50">
            {deleted.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`deleted-row-${u.email}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0B1A2F] truncate">{u.name} <span className="text-xs text-slate-400">· {u.email}</span></p>
                  <p className="text-xs text-slate-400">{u.days_remaining > 0 ? `${u.days_remaining} day${u.days_remaining === 1 ? "" : "s"} left to restore` : "Restore window elapsed"}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => restoreUser(u)} data-testid={`restore-user-${u.email}`}><RotateCcw size={14} className="mr-1" />Restore</Button>
                  <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => purgeUser(u)} data-testid={`purge-user-${u.email}`}><Trash2 size={14} className="mr-1" />Delete forever</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!toDelete} onOpenChange={(o) => { if (!o) { setToDelete(null); setDr(null); } }}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle>Delete user — {toDelete?.email}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This moves <strong>{toDelete?.name}</strong>'s login account to Recently deleted. Their access is revoked immediately, but you can restore it within 30 days. Any linked employee record and data are kept. To only suspend access, switch them to inactive instead.</p>
          {dr && dr.count > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="reassign-reports-block">
              <p className="text-sm text-amber-800"><strong>{dr.count}</strong> direct report{dr.count === 1 ? "" : "s"} report to {toDelete?.name}. Choose a new manager for them before deleting.</p>
              <ul className="text-xs text-amber-700 list-disc pl-5">{dr.reports.slice(0, 5).map((r) => <li key={r.id}>{r.name}</li>)}{dr.count > 5 && <li>+{dr.count - 5} more</li>}</ul>
              <div>
                <Label className="text-xs text-slate-600">Reassign reports to</Label>
                <Select value={reassignTo} onValueChange={setReassignTo}>
                  <SelectTrigger data-testid="reassign-manager-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Leave without a manager</SelectItem>
                    {dr.managers.map((m) => <SelectItem key={m.employee_id} value={m.employee_id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setToDelete(null); setDr(null); }}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={removeUser} data-testid="confirm-delete-btn"><Trash2 size={15} className="mr-1.5" />Delete user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reset} onOpenChange={(o) => !o && setReset(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password — {reset?.email}</DialogTitle></DialogHeader>
          <Input type="text" placeholder="New password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="reset-newpw-input" />
          <DialogFooter><Button onClick={doReset} className="bg-[#2EA3FD]" data-testid="confirm-reset-btn">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkUser} onOpenChange={(o) => !o && setLinkUser(null)}>
        <DialogContent data-testid="link-employee-dialog">
          <DialogHeader><DialogTitle>Employee link — {linkUser?.email}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Connect this login to an employee record so the person can view their own leave, details and Total Reward. One employee record per login.</p>
          <div>
            <Label className="text-xs text-slate-500">Linked employee</Label>
            <Select value={linkEmp} onValueChange={setLinkEmp}>
              <SelectTrigger data-testid="link-employee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not linked</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}{e.employee_code ? ` · ${e.employee_code}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {linkUser?.employee_id && <p className="text-xs text-slate-400 mt-1">Currently linked to {empName(linkUser.employee_id) || "an employee record"}.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkUser(null)}>Cancel</Button>
            <Button onClick={saveLink} className="bg-[#2EA3FD]" data-testid="confirm-link-btn">Save link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!perms} onOpenChange={(o) => !o && setPerms(null)}>
        <DialogContent data-testid="permissions-dialog">
          <DialogHeader><DialogTitle>Permissions — {perms?.name}</DialogTitle></DialogHeader>
          {perms && ((perms.role === "admin" || perms.role === "hr_admin") ? (
            <p className="text-sm text-slate-500">{ROLE_LABELS[perms.role]}s automatically have full access to all HR data (salary, cases, documents). Granular toggles apply to Managers and Employees.</p>
          ) : (
            <div className="space-y-1">
              <p className="text-sm text-slate-500 mb-2">Grant this user access to sensitive HR domains. By default salary is visible to Admins, HR Admins, a person's own line manager, and the employee themselves.</p>
              {PERMISSION_DEFS.map(([key, label]) => (
                <label key={key} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm" data-testid={`perm-row-${key}`}>
                  <span className="text-slate-700">{label}</span>
                  <Switch checked={(perms.permissions || []).includes(key)} onCheckedChange={() => togglePerm(key)} data-testid={`perm-toggle-${key}`} />
                </label>
              ))}
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
